import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React from 'react';
import { useRecoilValue } from 'recoil';
import rison from 'rison';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Description, EmptyState, EmptyStateIcon, Heading } from '@gitbook/spine-react';
import { useDebouncedFlush } from '@gitbook/util-react-hooks';

import { apiQuery } from '../../state';
import { stripObjectFromUndefined } from '../../utils';
import type { DeviceResolution } from './devices';
import { getSiteURLForContent, type SitePreviewContent } from './getSiteURLForContent';
import { Iframe } from './Iframe';
import { sitePreviewAtom } from './state';

/**
 * Iframe that displays the site preview.
 */
export function SitePreviewIframe(props: {
    site: api.Site;
    organization: api.Organization;

    /**
     * Content to display in the iframe.
     */
    content?: SitePreviewContent;

    /**
     * What to display when no preview is available.
     */
    fallback?: 'text' | 'illustration';

    /**
     * Customization settings to apply to the preview.
     */
    customization?: Partial<api.SiteCustomizationSettings>;

    /**
     * Visitor claims to apply to the preview.
     */
    claims?: api.PlainObject;

    /**
     * Class name to apply to the iframe.
     */
    className?: string;

    /**
     * If true, the user can interact with the iframe.
     */
    interactive?: boolean;

    /**
     * Screen resolution to use within the iframe.
     */
    scaleResolution?: DeviceResolution;
}) {
    const {
        site,
        organization,
        content = {},
        customization,
        claims,
        className,
        fallback = 'illustration',
        interactive = false,
        scaleResolution,
    } = props;

    const { data, isLoading } = useQuery(
        apiQuery(
            'getSitePublishingPreviewById',
            {
                organizationId: organization.id,
                siteId: site.id,
                siteSpace: content.siteSpaceId,
                ...(typeof claims !== 'undefined' ? { claims: rison.encode_object(claims) } : {}),
            },
            { nullOnError: true }
        )
    );

    const { theme } = useRecoilValue(sitePreviewAtom);
    const [previewURL, setPreviewURL] = React.useState<string | null>(null);

    const debounce = useDebouncedFlush(1000, {
        immediate: true,
    });

    // Update the iframe URL when the settings changed
    React.useEffect(() => {
        if (!data?.url) {
            return;
        }

        debounce(() => {
            const url = getSiteURLForContent(data.url, {
                siteSpaceId: content.siteSpaceId,
                revisionId: content.revisionId,
                pagePath: content.pagePath,
            });

            // rison does not like undefined values, but our customization logic can produce them. Strip them out before encoding.
            if (customization) {
                url.searchParams.append(
                    'customization',
                    rison.encode_object(stripObjectFromUndefined(customization))
                );
            }

            if (theme) {
                url.searchParams.append('theme', theme);
            }
            setPreviewURL(url.toString());
        });
    }, [
        data?.url,
        customization,
        theme,
        debounce,
        content.siteSpaceId,
        content.revisionId,
        content.pagePath,
    ]);

    if (process.env.GITBOOK_PREDICTABLE_UI) {
        return <div className={clsx('bg-muted', className)} />;
    }

    if (isLoading) {
        return <div className={clsx('shimmer', className)} />;
    }

    // Display the text fallback if no preview is available (e.g. when no space is added to the site)
    if (fallback === 'text' && !previewURL) {
        return (
            <div className={clsx('shimmer flex items-center justify-center', className)}>
                <EmptyState>
                    <EmptyStateIcon>
                        <Icon.EyeOff />
                    </EmptyStateIcon>
                    <Heading>No content in your site yet</Heading>
                    <Description>Add a space to your site to be able to preview it.</Description>
                </EmptyState>
            </div>
        );
    }

    return (
        <Iframe
            src={previewURL}
            interactive={interactive}
            scaleResolution={scaleResolution}
            className={className}
        />
    );
}
